import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Camera, CheckCircle2, ClipboardCopy, Download, Loader2, RotateCcw, RotateCw, Search, Send } from 'lucide-react';
import { toast } from 'sonner';
import API from '../../utils/apiBase';
import PeopleWorkspaceShell from '../../components/people/PeopleWorkspaceShell';
import { safeWorkforceSlug } from '../../lib/staffIntroductionInvitation';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
const statusLabel = {
  not_requested: 'Not requested',
  requested: 'Awaiting staff',
  pending_review: 'Needs manager review',
  changes_requested: 'Corrections requested',
  approved: 'Approved',
  withdrawn: 'Withdrawn',
};

export default function StaffIntroductionsPage() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState('');
  const [search, setSearch] = useState('');
  const [clientOrgId, setClientOrgId] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [inviteExpires, setInviteExpires] = useState('');
  const [clients, setClients] = useState([]);
  const [note, setNote] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoVersion, setPhotoVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [selectedForPack, setSelectedForPack] = useState([]);
  const [evidencePreview, setEvidencePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedControls, setSelectedControls] = useState([
    'identity', 'right_to_work', 'dbs', 'references',
    'mandatory_training', 'induction', 'professional_registration',
  ]);
  const [managerSummary, setManagerSummary] = useState('');
  const [reviewedSources, setReviewedSources] = useState(false);
  const [summaryApproved, setSummaryApproved] = useState(false);
  const [photoAuthorised, setPhotoAuthorised] = useState(false);
  const [releaseToClient, setReleaseToClient] = useState(false);
  const [namedRecipient, setNamedRecipient] = useState('');
  const blobRef = useRef(null);
  const current = items.find(item => item.employee_id === selected) || null;
  // One-use link is kept in manager memory only and never re-fetched from the server.
  const link = inviteLink;

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/staff-introductions`, { headers: auth(), timeout: 20000 });
      const rows = Array.isArray(data?.items) ? data.items : [];
      // Staff awaiting agency review belongs first, not buried in an A–Z roster.
      setItems(rows.sort((a, b) => (
        Number(b.status === 'pending_review') - Number(a.status === 'pending_review')
        || (b.submitted_at || '').localeCompare(a.submitted_at || '')
        || a.name.localeCompare(b.name)
      )));
      setSelected(old => old || rows[0]?.employee_id || '');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load staff profiles.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    let active = true;
    axios.get(`${API}/organisations`, { headers: auth(), timeout: 20000 })
      .then(({ data }) => {
        if (!active) return;
        setClients((data?.organisations || []).filter(org => (
          org?.id && org.is_active === true && org.relationship_stage === 'active_client'
        )));
      })
      .catch(() => toast.error('Could not load active clients. Profile requests remain disabled.'));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setEvidencePreview(null);
    setManagerSummary('');
    setReviewedSources(false);
    setSummaryApproved(false);
    setPhotoAuthorised(false);
    setReleaseToClient(false);
    if (!selected || !clientOrgId) return () => { active = false; };
    setPreviewLoading(true);
    axios.get(`${API}/staff-introductions/${encodeURIComponent(selected)}/assurance-preview`, {
      headers: auth(), timeout: 60000, params: { organisation_id: clientOrgId },
    }).then(({ data }) => {
      if (!active) return;
      setEvidencePreview(data);
      setManagerSummary(data.existing_professional_summary || '');
    }).catch((error) => {
      if (active) toast.error(error.response?.data?.detail || 'Could not check the current staff evidence.');
    }).finally(() => { if (active) setPreviewLoading(false); });
    return () => { active = false; };
  }, [selected, clientOrgId]);

  useEffect(() => {
    if (blobRef.current) { URL.revokeObjectURL(blobRef.current); blobRef.current = null; }
    setPhotoUrl('');
    if (!current?.has_photo) return;
    let active = true;
    axios.get(`${API}/staff-introductions/${encodeURIComponent(selected)}/photo`, {
      headers: auth(), responseType: 'blob',
      params: { approved: current.status === 'approved' },
    }).then(({ data }) => {
      if (!active) return;
      const url = URL.createObjectURL(data);
      blobRef.current = url;
      setPhotoUrl(url);
    }).catch(() => { /* Private preview unavailable, no fallback to public storage. */ });
    return () => {
      active = false;
      if (blobRef.current) { URL.revokeObjectURL(blobRef.current); blobRef.current = null; }
    };
  }, [selected, current?.submitted_at, current?.has_photo, current?.status, photoVersion]);

  const post = async (suffix, body, action) => {
    if (!selected || busy) return;
    setBusy(action);
    try {
      const { data } = await axios.post(`${API}/staff-introductions/${encodeURIComponent(selected)}/${suffix}`, body, {
        headers: auth(), timeout: 20000,
      });
      if (action === 'request' && data?.invite_token) {
        const slug = safeWorkforceSlug(data.workforce_slug);
        if (!slug) {
          setInviteLink('');
          toast.error('Invitation saved, but the agency workforce portal needs configuration. Contact your administrator.');
        } else {
          // Use the exact origin which the worker magic-login email uses:
          // other origins cannot share the short-lived browser continuation.
          const loginOrigin = new URL(data.frontend_url || window.location.origin);
          const localDev = loginOrigin.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(loginOrigin.hostname);
          if ((!localDev && loginOrigin.protocol !== 'https:') || loginOrigin.username || loginOrigin.password) {
            throw new Error('Invalid workforce portal origin');
          }
          const base = `${loginOrigin.origin}/worker/staff-introduction`;
          setInviteLink(`${base}?workspace=${encodeURIComponent(slug)}#invite=${encodeURIComponent(data.invite_token)}`);
          setInviteExpires(data.expires_at || '');
          toast.success('Private invitation created. Copy it securely now.');
        }
      } else {
        if (action === 'request') setInviteLink('');
        toast.success(action === 'request' ? 'Staff profile request saved' : 'Review saved');
      }
      setNote('');
      await refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'The action could not be completed.');
    } finally { setBusy(''); }
  };

  const request = () => post('request', { organisation_id: clientOrgId }, 'request');
  const review = (decision) => post('review', { decision, reason: note.trim() }, decision);

  const copy = async () => {
    if (!inviteLink) { toast.error('Create a new invitation first.'); return; }
    try {
      await navigator.clipboard.writeText(link);
      toast.success('One-use invitation copied. The correct worker must sign in first.');
    } catch { toast.error('Could not copy the link.'); }
  };

  const rotatePhoto = async (direction) => {
    if (!current || !['pending_review', 'approved'].includes(current.status) || busy) return;
    setBusy('rotate');
    try {
      await axios.post(`${API}/staff-introductions/${encodeURIComponent(selected)}/photo/rotate`,
        { direction }, { headers: auth(), timeout: 60000 });
      setPhotoVersion(value => value + 1);
      await refresh();
      toast.success(current.status === 'approved'
        ? 'Portrait corrected. A new approved PDF version is available.'
        : 'Private portrait rotated. Review the preview before approving.');
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail : 'Could not rotate the private portrait.');
    } finally { setBusy(''); }
  };

  const download = async () => {
    if (!current || busy) return;
    setBusy('pdf');
    try {
      const { data } = await axios.get(`${API}/staff-introductions/${encodeURIComponent(selected)}/pdf`, {
        headers: auth(), responseType: 'blob', timeout: 35000,
      });
      const fileUrl = URL.createObjectURL(data);
      const anchor = document.createElement('a');
      anchor.href = fileUrl;
      anchor.download = `Staff_Profile_${current.name.replace(/[^a-z0-9]+/gi, '_')}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Some browsers cancel a just-started download if the blob is revoked
      // synchronously. Release it after the browser has opened the file.
      window.setTimeout(() => URL.revokeObjectURL(fileUrl), 30_000);
      toast.success('Profile PDF downloaded. Check your browser Downloads folder.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'The approved PDF could not be generated.');
    } finally { setBusy(''); }
  };

  const togglePackStaff = (id) => {
    if (!selectedForPack.includes(id) && selectedForPack.length >= 12) {
      toast.error('Generate up to 12 individual staff PDFs per batch.');
      return;
    }
    setSelectedForPack(old => old.includes(id)
      ? old.filter(item => item !== id)
      : [...old, id]);
    // A different selected worker invalidates any previous individual release
    // sign-off. Focus the worker so the evidence preview matches the export.
    setSelected(id);
    setReviewedSources(false);
    setSummaryApproved(false);
    setPhotoAuthorised(false);
    setReleaseToClient(false);
    setNamedRecipient('');
  };

  const exportAssurancePacks = async () => {
    if (!clientOrgId || busy) return;
    const ids = selectedForPack.length ? selectedForPack : (selected ? [selected] : []);
    if (!ids.length || !selectedControls.length) {
      toast.error('Choose staff and at least one evidence section.');
      return;
    }
    if (releaseToClient && ids.length !== 1) {
      toast.error('Review and authorise each external release individually.');
      return;
    }
    // A checkbox selection and an evidence preview must refer to the SAME
    // person. Never use one worker's manager attestations for another worker.
    if (releaseToClient && (
      ids[0] !== selected || previewLoading
      || !evidencePreview || evidencePreview.employee_id !== ids[0]
    )) {
      toast.error('Open the selected staff member and review their current evidence before releasing.');
      return;
    }
    setBusy('assurance');
    try {
      const { data, headers } = await axios.post(`${API}/staff-introductions/assurance/export`, {
        organisation_id: clientOrgId, employee_ids: ids,
        selected_controls: selectedControls,
        include_internal_checklist: !releaseToClient,
        release_to_client: releaseToClient,
        recipient_name: namedRecipient.trim(),
        reviewed_source_records: reviewedSources,
        professional_summary_approved: summaryApproved,
        authorised_photo_use: photoAuthorised,
        summaries: selected && managerSummary.trim() ? { [selected]: managerSummary.trim() } : {},
      }, { headers: auth(), responseType: 'blob', timeout: 180000 });
      const url = URL.createObjectURL(data);
      const isZip = ids.length > 1;
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = isZip ? 'CareDock_Staff_Assurance_Packs.zip'
        : `Staff_Assurance_${(current?.name || 'Employee').replace(/[^a-z0-9]+/gi, '_')}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success(releaseToClient
        ? 'Recipient-specific PDF generated. Deliver only through an authorised secure channel.'
        : 'Internal review pack saved to Downloads. Not authorised for external release.');
    } catch (error) {
      let message = 'Could not generate the assurance pack.';
      if (error.response?.data instanceof Blob) {
        try { message = (await error.response.data.text() ? JSON.parse(await error.response.data.text()).detail : message) || message; }
        catch { /* keep generic error */ }
      } else if (typeof error.response?.data?.detail === 'string') message = error.response.data.detail;
      toast.error(message);
    } finally { setBusy(''); }
  };

  const visible = items.filter(row => (
    `${row.name} ${row.role} ${row.requested_for}`.toLowerCase().includes(search.toLowerCase())
  ));

  return (
    <PeopleWorkspaceShell
      activeView="staff_profiles"
      title="Staff profiles"
      description="Secure one-page staff introductions for care homes, separate from employment and compliance evidence."
      actions={
        <button type="button" onClick={refresh} className="inline-flex items-center gap-1 rounded-lg border bg-white px-3 py-2 text-sm">
          <RotateCcw size={16} /> Refresh
        </button>
      }
    >
      <section className="mx-auto max-w-7xl space-y-5 px-4 pb-6 text-slate-800 md:px-6">
      <section className="rounded-2xl border border-blue-200 bg-white p-5 shadow-sm" data-testid="admin-staff-assurance-generator">
        <h2 className="text-lg font-bold text-slate-900">Generate staff pack from CareDock</h2>
        <p className="mt-1 text-sm text-slate-600">
          Enterprise A4 letterhead, recorded staff identity and current evidence. No new worker invitation needed.
          Tick up to 12 workers in the list below for separate PDFs in a private ZIP.
        </p>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div>
            <label htmlFor="pack-client" className="text-sm font-semibold">Care-home recipient organisation</label>
            <select id="pack-client" value={clientOrgId} onChange={e => setClientOrgId(e.target.value)}
              className="mt-1 block w-full rounded-lg border px-3 py-2.5 text-sm">
              <option value="">Select active care home</option>
              {clients.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
            </select>
            <p className="mt-3 text-xs font-semibold">Include these sections (selection never means verification)</p>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {[
                ['identity', 'Identity'], ['right_to_work', 'Right to Work'],
                ['dbs', 'DBS suitability'], ['references', 'References'],
                ['mandatory_training', 'Mandatory training'], ['induction', 'Induction'],
                ['professional_registration', 'Professional registration'],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 rounded-lg border p-2 text-xs">
                  <input type="checkbox" checked={selectedControls.includes(key)}
                    onChange={e => setSelectedControls(old => e.target.checked
                      ? [...old, key] : old.filter(item => item !== key))} /> {label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-bold">Current staff evidence</h3>
            {previewLoading && <p className="text-xs text-blue-700">Checking live CareDock records…</p>}
            {evidencePreview && !previewLoading ? (
              <div className="mt-2 space-y-1 rounded-xl border p-3 text-xs">
                <p className="font-semibold">{evidencePreview.name} · {evidencePreview.role}</p>
                <p>Photograph: {evidencePreview.photo_available ? 'On record; confirm sharing authority' : 'Missing — request photograph'}</p>
                <p>Introduction for this care home: {evidencePreview.approved_introduction ? 'Approved' : 'Not yet approved'}</p>
                {Object.entries(evidencePreview.controls || {}).filter(([key]) => selectedControls.includes(key)).map(([key, row]) => (
                  <div key={key} className="flex justify-between gap-2 border-t py-1">
                    <span>{row.label}</span>
                    <span className={row.verified ? 'font-bold text-emerald-700' : 'font-bold text-amber-800'}>{row.status}</span>
                  </div>
                ))}
                <p className={evidencePreview.is_work_ready ? 'font-bold text-emerald-700' : 'font-bold text-red-700'}>
                  {evidencePreview.is_work_ready ? 'Readiness checks currently clear' : `${evidencePreview.blocker_count} unresolved readiness blockers — HOLD`}
                </p>
              </div>
            ) : <p className="mt-2 text-xs text-slate-500">Select a care home and staff member to load their verification status.</p>}
            <label htmlFor="pack-summary" className="mt-3 block text-sm font-semibold">Factual professional summary (only if missing)</label>
            <textarea id="pack-summary" rows={3} maxLength={950} value={managerSummary}
              onChange={e => setManagerSummary(e.target.value)}
              className="mt-1 w-full rounded-xl border p-3 text-sm"
              placeholder="Use supported experience from CareDock. Never invent work history." />
            <p className="text-xs text-slate-500">An existing approved summary is reused. Missing text stays marked as pending in internal drafts.</p>
          </div>
        </div>
        <div className="mt-4 grid gap-2 rounded-xl border bg-slate-50 p-3 text-sm">
          <p className="text-xs font-semibold text-slate-800">Evidence preview and confirmations apply to: {current?.name || 'Select staff'}. For a client release, choose only this person.</p>
          <label className="flex gap-2"><input type="checkbox" checked={reviewedSources} onChange={e => setReviewedSources(e.target.checked)} />
            I have checked the underlying current source records and restrictions.</label>
          <label className="flex gap-2"><input type="checkbox" checked={summaryApproved} onChange={e => setSummaryApproved(e.target.checked)} />
            I have reviewed this professional introduction for accuracy.</label>
          <label className="flex gap-2"><input type="checkbox" checked={photoAuthorised} onChange={e => setPhotoAuthorised(e.target.checked)} />
            I confirm the photograph is appropriate and authorised for this placement.</label>
          <label className="flex gap-2 font-semibold text-blue-900"><input type="checkbox" checked={releaseToClient} onChange={e => setReleaseToClient(e.target.checked)} />
            Request client-release PDF (one person only, all checks must be current)</label>
          {releaseToClient && (
            <label htmlFor="pack-recipient" className="text-xs font-semibold">Named authorised recipient
              <input id="pack-recipient" value={namedRecipient} maxLength={100}
                onChange={e => setNamedRecipient(e.target.value)}
                className="mt-1 block w-full rounded-lg border bg-white px-3 py-2.5 text-sm"
                placeholder="Care-home manager's full name" />
            </label>
          )}
          <p className="text-xs text-slate-600">
            Ticking never approves an unchecked DBS, RTW or qualification. Batch packs contain an INTERNAL checklist
            and must not be sent to a care home. CareDock does not email or publish generated packs.
          </p>
        </div>
        <button type="button" onClick={exportAssurancePacks}
          disabled={!!busy || !clientOrgId || !selectedControls.length || (!selected && !selectedForPack.length)}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-blue-700 px-4 py-3 text-sm font-bold text-white disabled:opacity-50">
          <Download size={18} /> {busy === 'assurance' ? 'Generating…' : selectedForPack.length > 1 ? 'Download staff ZIP' : 'Generate staff pack'}
        </button>
      </section>
      <div className="grid gap-5 lg:grid-cols-[310px_minmax(0,1fr)]">
        <aside className="rounded-2xl border bg-white p-3">
          <label className="flex items-center gap-2 rounded-lg border px-3 py-2 text-slate-500">
            <Search size={17} />
            <input aria-label="Search staff" className="min-w-0 flex-1 outline-none" placeholder="Find a staff member"
              value={search} onChange={e => setSearch(e.target.value)} />
          </label>
          <p className="px-2 pt-3 text-xs text-slate-500">{visible.length} staff records · {items.filter(item => item.status === 'pending_review').length} awaiting manager review</p>
          <p className="px-2 pt-1 text-xs text-slate-500">Profiles awaiting review appear first. Search by staff name.</p>
          <div className="mt-2 flex flex-wrap gap-2 px-1">
            <button type="button" onClick={() => setSelectedForPack(visible.slice(0,12).map(item => item.employee_id))}
              className="rounded-lg border px-2.5 py-1.5 text-xs font-semibold">Select first 12 visible</button>
            <button type="button" onClick={() => setSelectedForPack([])}
              className="rounded-lg border px-2.5 py-1.5 text-xs">Clear selection</button>
          </div>
          <div className="mt-2 max-h-[560px] space-y-1 overflow-y-auto">
            {loading && <div className="flex items-center gap-2 p-3"><Loader2 className="animate-spin" /> Loading…</div>}
            {visible.map(item => (
              <div key={item.employee_id} className={`flex items-start gap-2 rounded-xl border px-2 py-2 ${selected === item.employee_id ? 'border-blue-300 bg-blue-50' : 'border-transparent hover:bg-slate-50'}`}>
                <label className="mt-2" title="Add this person to a PDF export">
                  <input type="checkbox" aria-label={`Include ${item.name} in staff pack`}
                    checked={selectedForPack.includes(item.employee_id)}
                    onChange={() => togglePackStaff(item.employee_id)} />
                </label>
                <button type="button"
                  onClick={() => { setSelected(item.employee_id); setClientOrgId(old => old || item.client_org_id || ''); setInviteLink(''); setInviteExpires(''); }}
                  className="min-w-0 flex-1 p-1 text-left">
                  <span className="block truncate font-semibold">{item.name}</span>
                  <span className="block truncate text-xs text-slate-500">{item.role}</span>
                  <span className="mt-1 block text-xs text-blue-700">{statusLabel[item.status] || item.status}</span>
                </button>
              </div>
            ))}
          </div>
        </aside>
        <article className="rounded-2xl border bg-white p-5 shadow-sm md:p-7">
          {!current ? <p className="text-slate-600">Select a staff member to start.</p> : (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center gap-5">
                {photoUrl ? (
                  <img src={photoUrl} alt={`Portrait submitted by ${current.name}`} className="h-32 w-28 rounded-lg border bg-slate-50 object-contain" />
                ) : (
                  <div className="flex h-32 w-28 items-center justify-center rounded-lg bg-slate-100 text-slate-400"><Camera size={34} /></div>
                )}
                <div>
                  <h2 className="text-xl font-bold">{current.name}</h2>
                  <p className="text-sm text-slate-500">{current.role}</p>
                  <p className="mt-2 text-sm font-semibold text-blue-700">{statusLabel[current.status] || current.status}</p>
                  {current.approved_at && <p className="mt-1 text-xs text-slate-500">Last approved: {current.approved_at.slice(0, 10)}</p>}
                  {current.status === 'pending_review' && (
                    <p className="mt-2 max-w-sm rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-950">
                      Submission received. Review the photo and statement below, then approve. The PDF download will appear here after approval.
                    </p>
                  )}
                  {['requested', 'changes_requested'].includes(current.status) && (
                    <p className="mt-2 max-w-sm text-xs text-slate-600">
                      No approved PDF yet. The staff member must finish or correct their submission before it can be reviewed.
                    </p>
                  )}
                  {['pending_review', 'approved'].includes(current.status) && current.has_photo && (
                    <div className="mt-2">
                      <p className="text-xs text-slate-600">Photo sideways? Rotate it without uploading again. Approved profiles receive a new, auditable PDF version.</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button type="button" onClick={() => rotatePhoto('left')} disabled={!!busy}
                          className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs disabled:opacity-50">
                          <RotateCcw size={14} /> Rotate left
                        </button>
                        <button type="button" onClick={() => rotatePhoto('right')} disabled={!!busy}
                          className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs disabled:opacity-50">
                          <RotateCw size={14} /> Rotate right
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="rounded-xl border bg-slate-50 p-4">
                <h3 className="font-semibold">Optional: request a missing portrait or worker introduction</h3>
                <p className="mt-1 text-sm text-slate-600">This is only needed when the staff member has no usable photograph or confirmed introduction. Admin can generate existing-record packs above without requesting re-uploads.</p>
                <label className="mt-3 block text-xs font-medium" htmlFor="client-home">Approved care home / client</label>
                <select id="client-home" value={clientOrgId} onChange={e => setClientOrgId(e.target.value)}
                  className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm">
                  <option value="">Select a current client</option>
                  {clients.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
                </select>
                {clients.length === 0 && <p className="mt-2 text-xs text-amber-800">No active CareDock clients available. Create or activate a client in Organisations before requesting an introduction.</p>}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" onClick={request} disabled={!!busy || !clients.some(org => org.id === clientOrgId) || current.status === 'pending_review'}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
                    <Send size={16} /> Create private invitation
                  </button>
                  <button type="button" onClick={copy} disabled={!inviteLink}
                    className="inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold disabled:opacity-50">
                    <ClipboardCopy size={16} /> Copy one-use link
                  </button>
                </div>
                {inviteLink && <p className="mt-3 text-xs text-slate-600">The invitation is ready to copy. It expires {inviteExpires ? new Date(inviteExpires).toLocaleDateString('en-GB') : 'in 7 days'}. For privacy, the secret is shown only immediately after creation, not after leaving this page.</p>}
                <p className="mt-1 text-xs text-slate-500">Send the copied link privately. The worker must sign in to their own CareDock account, then reopen the link to redeem it. Do not send any original compliance files.</p>
              </div>
              {current.summary && (
                <section className="rounded-xl border p-4">
                  <h3 className="font-semibold">2. Review professional introduction</h3>
                  <p className="mt-2 text-xs text-slate-500">Name and job title come from the CareDock staff record. Review the worker's statement against their saved work history before approving; compliance checks are handled separately.</p>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed">{current.summary}</p>
                  {current.status === 'pending_review' && (
                    <div className="mt-5 space-y-3">
                      <label className="block text-xs font-semibold" htmlFor="review-note">Correction note (required when requesting changes)</label>
                      <textarea id="review-note" rows={3} maxLength={500} value={note} onChange={e => setNote(e.target.value)}
                        placeholder="Tell the staff member what needs to change"
                        className="w-full rounded-lg border p-3 text-sm" />
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => review('approve')} disabled={!!busy || !current.has_photo}
                          className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">
                          <CheckCircle2 size={16} /> Approve one-page profile
                        </button>
                        <button onClick={() => review('changes_requested')} disabled={!!busy || !note.trim()}
                          className="rounded-lg border px-4 py-2.5 text-sm disabled:opacity-50">
                          Request corrections
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              )}
              {current.approved_at && current.status === 'approved' && (
                <section className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                  <h3 className="font-semibold text-blue-950">3. Approved profile</h3>
                  <p className="mt-1 text-sm text-slate-700">Download a one-page care-home introduction with the CareDock name and job title, approved portrait and summary, and tenant letterhead. The PDF is generated privately here after approval; it is not in the general Documents list. NI, DOB, DBS and Right to Work identifiers are excluded.</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button onClick={download} disabled={!!busy}
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2.5 font-semibold text-white disabled:opacity-50">
                      <Download size={17} /> Download approved PDF
                    </button>
                    <button onClick={() => review('withdraw')} disabled={!!busy} className="rounded-lg border px-4 py-2.5 text-sm">Withdraw publication</button>
                  </div>
                </section>
              )}
              <p className="text-xs text-slate-500">
                Staff introductions never change onboarding, Right to Work, DBS, qualifications, shift eligibility or payroll.
                Never send the full Employment Pack or evidence vault to a care home.
              </p>
            </div>
          )}
        </article>
      </div>
      </section>
    </PeopleWorkspaceShell>
  );
}
